import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, Plus, X, Library, Download, Check } from 'lucide-react'
import api from '../../services/api'

const AUDIT_TYPES = ['whs', 'environmental', 'quality', 'contractor', 'fire_safety', 'emergency']

type SectionItem = { question: string; requirement: string; regulation_ref: string; weight: number }
type Section = { title: string; items: SectionItem[] }

const blankItem = (): SectionItem => ({ question: '', requirement: '', regulation_ref: '', weight: 1 })
const blankSection = (): Section => ({ title: '', items: [blankItem()] })

export default function AuditTemplates() {
  const navigate = useNavigate()
  const [templates, setTemplates] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState({
    name: '', description: '', audit_type: 'whs', frequency: 'annual', pass_threshold: 80,
    sections: [blankSection()] as Section[],
  })
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')
  const [showLibrary, setShowLibrary] = useState(false)
  const [libraryItems, setLibraryItems] = useState<any[]>([])
  const [libraryLoading, setLibraryLoading] = useState(false)
  const [importing, setImporting] = useState<string | null>(null)
  const [imported, setImported] = useState<Set<string>>(new Set())

  const load = () => {
    api.get('/audits/templates').then(r => setTemplates(r.data.data || [])).finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  const addSection = () => setForm(f => ({ ...f, sections: [...f.sections, blankSection()] }))
  const removeSection = (i: number) => setForm(f => ({ ...f, sections: f.sections.filter((_, idx) => idx !== i) }))
  const updateSection = (i: number, field: keyof Section, val: any) =>
    setForm(f => ({ ...f, sections: f.sections.map((s, idx) => idx === i ? { ...s, [field]: val } : s) }))

  const addItem = (si: number) => setForm(f => ({
    ...f, sections: f.sections.map((s, i) => i === si ? { ...s, items: [...s.items, blankItem()] } : s),
  }))
  const removeItem = (si: number, qi: number) => setForm(f => ({
    ...f, sections: f.sections.map((s, i) => i === si ? { ...s, items: s.items.filter((_, idx) => idx !== qi) } : s),
  }))
  const updateItem = (si: number, qi: number, field: keyof SectionItem, val: any) => setForm(f => ({
    ...f, sections: f.sections.map((s, i) => i === si ? {
      ...s, items: s.items.map((it, idx) => idx === qi ? { ...it, [field]: val } : it),
    } : s),
  }))

  const openLibrary = async () => {
    setShowLibrary(true)
    if (libraryItems.length) return
    setLibraryLoading(true)
    try {
      const { data } = await api.get('/audits/templates/library')
      setLibraryItems(data)
    } catch {}
    setLibraryLoading(false)
  }

  const importTemplate = async (slug: string) => {
    setImporting(slug)
    try {
      await api.post(`/audits/templates/library/${slug}/import`, {})
      setImported(prev => new Set([...prev, slug]))
      load()
    } catch {}
    setImporting(null)
  }

  const create = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) { setCreateError('Name is required'); return }
    const validSections = form.sections.filter(s => s.title.trim() && s.items.some(it => it.question.trim()))
    if (validSections.length === 0) { setCreateError('Add at least one section with one question'); return }
    setCreating(true)
    setCreateError('')
    try {
      await api.post('/audits/templates', {
        ...form,
        sections: validSections.map(s => ({
          ...s,
          items: s.items.filter(it => it.question.trim()),
        })),
      })
      setShowCreate(false)
      setForm({ name: '', description: '', audit_type: 'whs', frequency: 'annual', pass_threshold: 80, sections: [blankSection()] })
      load()
    } catch (err: any) {
      setCreateError(err.response?.data?.error || 'Failed to create')
    }
    setCreating(false)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Audit Templates</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>{templates.length} templates</p>
        </div>
        <div className="flex gap-2">
          <button onClick={openLibrary} className="btn btn-secondary"><Library size={16} /> Browse Library</button>
          <button onClick={() => navigate('/audits/templates/new')} className="btn btn-primary"><Plus size={16} /> New Template</button>
          <button onClick={() => setShowCreate(true)} className="btn btn-secondary"><Plus size={16} /> Quick Create</button>
        </div>
      </div>

      {loading ? <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>Loading...</div> : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(t => (
            <div key={t.id} className="card space-y-2">
              <div className="flex items-start justify-between">
                <p className="font-semibold" style={{ color: 'var(--text)' }}>{t.name}</p>
                <span className="badge badge-medium capitalize">{t.audit_type?.replace('_', ' ')}</span>
              </div>
              {t.description && <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t.description}</p>}
              <div className="flex gap-4 text-xs" style={{ color: 'var(--text-muted)' }}>
                <span>Pass: {t.pass_threshold}%</span>
                {t.frequency && <span className="capitalize">{t.frequency}</span>}
              </div>
            </div>
          ))}
          {templates.length === 0 && (
            <div className="md:col-span-3 text-center py-16" style={{ color: 'var(--text-muted)' }}>
              No templates yet — create one to enable structured auditing
            </div>
          )}
        </div>
      )}

      {/* Library modal */}
      {showLibrary && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="w-full max-w-3xl rounded-2xl shadow-xl my-4" style={{ background: 'var(--white)' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 rounded-t-2xl" style={{ borderColor: 'var(--border)', background: 'var(--white)' }}>
              <div>
                <h2 className="font-semibold text-lg" style={{ color: 'var(--text)' }}>AU Standard Template Library</h2>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>10 pre-built templates aligned to Australian WHS legislation</p>
              </div>
              <button onClick={() => setShowLibrary(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <div className="px-6 py-5">
              {libraryLoading ? (
                <div className="text-center py-10" style={{ color: 'var(--text-muted)' }}>Loading library…</div>
              ) : (
                <div className="grid md:grid-cols-2 gap-3">
                  {libraryItems.map(item => {
                    const done = imported.has(item.slug)
                    const busy = importing === item.slug
                    return (
                      <div key={item.slug} className="p-4 rounded-xl" style={{ border: '1px solid var(--border)', background: done ? 'var(--bg)' : 'white' }}>
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p className="font-semibold text-sm" style={{ color: 'var(--text)' }}>{item.name}</p>
                          <span className="badge badge-medium capitalize flex-shrink-0">{item.audit_type.replace('_', ' ')}</span>
                        </div>
                        <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>{item.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                            {item.section_count} sections · {item.question_count} questions · {item.estimated_duration_mins}min
                          </span>
                          <button
                            onClick={() => !done && importTemplate(item.slug)}
                            disabled={busy || done}
                            className="btn btn-secondary text-xs py-1.5 flex items-center gap-1"
                            style={{ opacity: busy ? 0.6 : 1, background: done ? 'var(--green-light)' : undefined, color: done ? 'var(--green)' : undefined }}
                          >
                            {done ? <><Check size={12} /> Imported</> : busy ? 'Importing…' : <><Download size={12} /> Import</>}
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="w-full max-w-3xl rounded-2xl shadow-xl my-4" style={{ background: 'var(--white)' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 rounded-t-2xl" style={{ borderColor: 'var(--border)', background: 'var(--white)' }}>
              <h2 className="font-semibold text-lg" style={{ color: 'var(--text)' }}>New Audit Template</h2>
              <button onClick={() => setShowCreate(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <form onSubmit={create} className="px-6 py-5 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Template Name *</label>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Monthly WHS Site Inspection" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Type</label>
                  <select value={form.audit_type} onChange={e => setForm(f => ({ ...f, audit_type: e.target.value }))}>
                    {AUDIT_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Frequency</label>
                  <select value={form.frequency} onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))}>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="biannual">Biannual</option>
                    <option value="annual">Annual</option>
                    <option value="ad_hoc">Ad Hoc</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Pass Threshold (%)</label>
                  <input type="number" min={0} max={100} value={form.pass_threshold} onChange={e => setForm(f => ({ ...f, pass_threshold: parseInt(e.target.value) || 80 }))} />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Description</label>
                <textarea rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </div>

              {/* Sections */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-sm" style={{ color: 'var(--text)' }}>Sections & Questions</p>
                  <button type="button" onClick={addSection} className="btn btn-secondary text-xs py-1.5 flex items-center gap-1"><Plus size={12} /> Section</button>
                </div>
                {form.sections.map((sec, si) => (
                  <div key={si} className="space-y-3 p-4 rounded-xl" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                    <div className="flex gap-2">
                      <input placeholder={`Section ${si + 1} title`} value={sec.title} onChange={e => updateSection(si, 'title', e.target.value)} className="flex-1" />
                      {form.sections.length > 1 && (
                        <button type="button" onClick={() => removeSection(si)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={16} /></button>
                      )}
                    </div>
                    {sec.items.map((item, qi) => (
                      <div key={qi} className="flex gap-2 items-start">
                        <span className="text-xs mt-2.5 flex-shrink-0" style={{ color: 'var(--text-muted)' }}>{qi + 1}.</span>
                        <div className="flex-1 space-y-1.5">
                          <input placeholder="Question / requirement to check" value={item.question} onChange={e => updateItem(si, qi, 'question', e.target.value)} />
                          <div className="grid grid-cols-2 gap-2">
                            <input placeholder="Regulation reference" value={item.regulation_ref} onChange={e => updateItem(si, qi, 'regulation_ref', e.target.value)} />
                            <input placeholder="Specific requirement" value={item.requirement} onChange={e => updateItem(si, qi, 'requirement', e.target.value)} />
                          </div>
                        </div>
                        {sec.items.length > 1 && (
                          <button type="button" onClick={() => removeItem(si, qi)} className="mt-2.5 flex-shrink-0" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={14} /></button>
                        )}
                      </div>
                    ))}
                    <button type="button" onClick={() => addItem(si)} className="flex items-center gap-1 text-xs" style={{ color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer' }}>
                      <Plus size={12} /> Add question
                    </button>
                  </div>
                ))}
              </div>

              {createError && (
                <div className="flex items-start gap-2 text-sm p-3 rounded-lg" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>
                  <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />{createError}
                </div>
              )}

              <div className="flex gap-3 pb-2">
                <button type="submit" className="btn btn-primary" disabled={creating}>{creating ? 'Creating...' : 'Create Template'}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
